import { Controller } from "@hotwired/stimulus";

// Two-layer cookie consent. Nothing outside the essential category runs without an
// explicit yes: gated scripts render as inert text/plain blobs (see
// shared/_consent_scripts) and are cloned into executable scripts only once their
// category is granted.
//
// The one exception is a visitor the server marks as implied (ConversionTracking):
// marketing runs without the banner until they refuse it in the settings.
//
// The choice lives in a cookie rather than localStorage so it carries across
// subdomains and can be read server side if a view ever needs it.
const COOKIE = "_oremus_consent";
const ONE_YEAR = 31536000;
const FORMAT = /^v1\.m([01])\.f([01])$/;
const CATEGORIES = ["marketing", "functional"];
// Written only under marketing consent: our campaign cookies and the ones the
// Meta pixel sets on this domain. Withdrawing consent removes them.
const MARKETING_COOKIES = /^(_oremus_(utm_\w+|fbc|ref)|_fbp|_fbc)$/;

export default class extends Controller {
  static targets = ["banner", "modal"];
  static values = { implied: Boolean };

  connect() {
    this.closeOnEscape = (event) => {
      if (event.key === "Escape" && !this.modalTarget.hidden) this.close();
    };
    document.addEventListener("keydown", this.closeOnEscape);

    const stored = this.read();
    if (stored) this.apply(stored);
    else if (this.impliedValue) this.apply(this.unasked());
    else this.bannerTarget.hidden = false;
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeOnEscape);
  }

  open() {
    const current = this.read() || this.unasked();
    this.switches().forEach((input) => {
      input.checked = current[input.dataset.consentCategory] === true;
    });
    this.modalTarget.hidden = false;
  }

  close() {
    this.modalTarget.hidden = true;
  }

  acceptAll() {
    this.decide({ marketing: true, functional: true });
  }
  rejectAll() {
    this.decide(this.noConsent());
  }

  save() {
    const consent = this.noConsent();
    this.switches().forEach((input) => {
      consent[input.dataset.consentCategory] = input.checked;
    });
    this.decide(consent);
  }

  decide(consent) {
    this.write(consent);
    if (!consent.marketing) this.forgetMarketing();
    this.apply(consent);
    this.bannerTarget.hidden = true;
    this.modalTarget.hidden = true;
  }

  // Granting a category is one-way within a page load: scripts already running cannot be
  // unloaded, so withdrawing consent takes effect on the next page view.
  apply(consent) {
    CATEGORIES.filter((category) => consent[category]).forEach((category) =>
      this.activate(category),
    );
  }

  activate(category) {
    const selector = `script[type="text/plain"][data-consent="${category}"]`;
    document.querySelectorAll(selector).forEach((blob) => {
      if (blob.dataset.consentDone) return;
      blob.dataset.consentDone = "1";
      const script = document.createElement("script");
      script.text = blob.textContent;
      document.head.appendChild(script);
    });
  }

  // The pixel writes its cookies for the whole site (.oremus.fm), so each is
  // expired both there and on the exact host.
  forgetMarketing() {
    const site = window.location.hostname.replace(/^www\./, "");
    document.cookie.split("; ").map((pair) => pair.split("=")[0])
      .filter((name) => MARKETING_COOKIES.test(name))
      .forEach((name) => {
        document.cookie = `${name}=;path=/;max-age=0`;
        document.cookie = `${name}=;path=/;max-age=0;domain=.${site}`;
      });
  }

  switches() {
    return this.modalTarget.querySelectorAll("input[data-consent-category]");
  }

  noConsent() {
    return { marketing: false, functional: false };
  }

  // What holds before the visitor has chosen anything.
  unasked() {
    return { marketing: this.impliedValue, functional: false };
  }

  read() {
    const match = document.cookie.match(`(?:^|; )${COOKIE}=([^;]*)`);
    if (!match) return null;
    const parsed = decodeURIComponent(match[1]).match(FORMAT);
    return parsed
      ? { marketing: parsed[1] === "1", functional: parsed[2] === "1" }
      : null;
  }

  write(consent) {
    const value = `v1.m${consent.marketing ? 1 : 0}.f${consent.functional ? 1 : 0}`;
    const secure = window.location.protocol === "https:" ? ";Secure" : "";
    document.cookie = `${COOKIE}=${value};path=/;max-age=${ONE_YEAR};SameSite=Lax${secure}`;
  }
}
